import { Close } from '@mui/icons-material';
import { Box, Button, Checkbox, FormControlLabel, IconButton, Modal, Stack } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import AuthSelect from '../../../components/AuthController/AuthSelect';
import AuthDatePicker from '../../../components/AuthController/AuthDatePicker';
import AuthInput from '../../../components/AuthController/AuthInput';
import { useDispatch, useSelector } from 'react-redux';
import AuthLocation from '../../../components/AuthController/AuthLocation';
import { basicProfilecategory, genderOptions } from '../../../../constants';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMessage } from '../../../../base/MessageProvider';
import { matchIsValidTel } from 'mui-tel-input';
import { Bars } from 'react-loader-spinner';
import clsx from 'clsx';
import { updateSignupProfile } from '../../../actions/auth';
import { StorageConstants } from '../../../storage/storageConstants';

const boxStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 400,
    width: '90%',
    bgcolor: 'white',
    paddingX: 5,
    paddingY: 3,
    borderRadius: '18px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center' 
}
  
const validationSchema = Yup.object().shape({
      category: Yup.string().required('Profile category is required'),
      gender: Yup.string().required('Gender is required'),
      dob: Yup.string().required('Date of Birth is required'),
      email: Yup.string().trim().email("Invalid email id").required('Email id is required'),
      isActor: Yup.boolean(), isModel: Yup.boolean(), isInfluencer: Yup.boolean(),
      instagramHandle: Yup.string()
        .when(['category', 'isInfluencer'], (values, schema) => {
            if (values[0] === basicProfilecategory.influencer || values[1])
                return schema.required('Instagram user id is required')
            return schema;
        }),
      location: Yup.string().required('Location is required'),
});

const BasicInfoModal = ({ open, onClose }) => {
    const navigate = useNavigate();
    const alert = useMessage();
    const dispatch = useDispatch();

    const locationList = useSelector(state => state.artistGeneralReducer?.locationList) ?? [];
    const categoryList = useSelector(state => state.artistGeneralReducer?.profileCategoryList) ?? [];
    const loading = useSelector((state) => state.artistAuthReducer?.loading);
    const mobileNo = useSelector((state) => state.artistAuthReducer?.mobileNo);

    const categoryOptions = useMemo(() => categoryList.map((item) => ({
        label: item.name, value: item.id
    })), [categoryList]);

    const {
        control, clearErrors, watch, setValue,
        formState: { errors }, reset, handleSubmit
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            category: '',
            gender: '',
            dob: null,
            email: '',
            location: '',
            imdbLink: '',
            instagramHandle: ''
        },
    });

    const selectedCategory = watch('category');
    const isActor = selectedCategory === basicProfilecategory.actor;
    const isModel = selectedCategory === basicProfilecategory.model;
    const isInfluencer = selectedCategory === basicProfilecategory.influencer;

    useEffect(() => {
        setValue('isActor', false);
        setValue('isModel', false);
        setValue('isInfluencer', false);
    }, [selectedCategory])

    const onChangeCheckValue = (key, event) => {
        setValue(key, event?.target.checked);
    }

    useEffect(() => {
        const checkAuthentications = async () => {
            const signupToken = localStorage.getItem(StorageConstants.SIGNUP_TOKEN);
            if (!signupToken) {
                navigate('/artist/auth/join', { replace: true });
                return;
            }
            clearErrors();
            reset();
        }
        checkAuthentications(); 
    }, []);

    const onSubmit = async (data) => {
        clearErrors();
        dispatch(updateSignupProfile({ ...data, mobileNo }, (res) => {
            alert.showSuccess(res?.message);
            navigate('/artist/auth/profile-pic', { replace: true });
        }, (error) => {
            alert.showError(error?.message ?? "Something went wrong!!");
        }))
    }


    return (
        <Modal disableAutoFocus sx={{ bgcolor: '#F4F4F499'}} hideBackdrop open={open}>
            <Box className="shadow-[0_0_14px_grey]" sx={boxStyle}>
                <IconButton onClick={onClose} className='absolute top-3 right-3' size='small'> 
                    <Close className='text-xl' />
                </IconButton>
                <Box className="mb-6">
                    <h2 className="font-['Poppins-Medium'] font-[500] text-[16px]">BASIC INFO</h2>
                </Box>
                <Stack className="w-full" spacing={2} my={1}>
                    <AuthSelect
                        placeholder='Select Category*' 
                        hookFormControl={control} 
                        varName="category" 
                        options={categoryOptions} size='small'
                        errorMessage={errors?.category?.message}
                    />
                    {selectedCategory && (
                        <Stack direction={'row'} spacing={1} alignItems={'center'} justifyContent={'space-between'}>
                            {isModel && (
                                <FormControlLabel
                                    control={
                                        <Checkbox 
                                            checked={watch('isActor')} onChange={onChangeCheckValue?.bind(this, "isActor")} 
                                            sx={{ '&.Mui-checked': { color: "#2F80ED" } }} size='small'
                                        />
                                    } 
                                    componentsProps={{ typography: { className: "font-['Poppins-Regular'] font-[400] text-[11px] text-[#1F1E1E]" }}}  label="Are you an Actor?" 
                                />
                            )}
                            {isActor && (
                                <FormControlLabel 
                                    control={
                                        <Checkbox 
                                            checked={watch('isModel')} onChange={onChangeCheckValue?.bind(this, "isModel")} 
                                            sx={{ '&.Mui-checked': { color: "#2F80ED" } }} size='small'
                                        />
                                    } 
                                    componentsProps={{ typography: { className: "font-['Poppins-Regular'] font-[400] text-[11px] text-[#1F1E1E]" }}}  label="Are you a Model?" 
                                />
                            )}
                            {!isInfluencer && (
                                <FormControlLabel 
                                    control={
                                        <Checkbox 
                                            checked={watch('isInfluencer')} onChange={onChangeCheckValue?.bind(this, "isInfluencer")} 
                                            sx={{ '&.Mui-checked': { color: "#2F80ED" } }} size='small'
                                        />
                                    } 
                                    componentsProps={{ typography: { className: "font-['Poppins-Regular'] font-[400] text-[11px] text-[#1F1E1E]" }}}  label="Are you an Influencer?" 
                                />
                            )}
                        </Stack>
                    )}
                    <AuthSelect
                        placeholder='Select Gender*' 
                        hookFormControl={control} 
                        varName="gender" 
                        options={genderOptions} size='small'
                        errorMessage={errors?.gender?.message}
                    />
                    <AuthDatePicker
                        placeholder='Select D.O.B*' 
                        hookFormControl={control} 
                        varName="dob" 
                        size='small'
                        maxDate={new Date()}
                        errorMessage={errors?.dob?.message}
                    />
                    <AuthInput
                        placeholder='Enter Email*' 
                        hookFormControl={control} 
                        varName="email" 
                        size='small'
                        errorMessage={errors?.email?.message}
                    />
                    <AuthLocation 
                        placeholder='Enter Location*' 
                        hookFormControl={control} 
                        varName="location" 
                        size='small'
                        errorMessage={errors?.location?.message}
                        options={locationList} 
                    />
                    {(watch('isInfluencer') || isInfluencer) && (
                        <AuthInput
                            placeholder='Enter Instagram Handle' 
                            hookFormControl={control} 
                            varName="instagramHandle" 
                            size='small'
                            errorMessage={errors?.instagramHandle?.message}
                        />
                    )}
                </Stack>
                <Button onClick={handleSubmit(onSubmit)} disabled={loading} className={clsx("bg-[#2F80ED] flex gap-3 justify-center text-[#FFF] w-full rounded-full my-2 font-['Poppins-Medium'] font-[500]", { "bg-opacity-70": loading })}>
                    Submit
                    <Bars
                        height="20"
                        width={'20'}
                        color="#FFFFFF"
                        ariaLabel="bars-loading"
                        visible={loading}
                    />
                </Button>
            </Box>
        </Modal>
    );
}

export default React.memo(BasicInfoModal, (prevProps, nextProps) => {
    return prevProps.open === nextProps.open
});